import { useMailOrderStore } from '../../../../../stores/useMailOrderStore';

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="col-4 text-muted ms-3">{label}</div>
      <div className="col-8 fw-semibold ms-3">{value || '—'}</div>
    </div>
  );
}

function trueKeys(obj: Record<string, boolean | string>): string {
  return Object.entries(obj)
    .filter(([k, v]) => v === true && k !== 'none')
    .map(([k]) => k.charAt(0).toUpperCase() + k.slice(1))
    .join(', ');
}

export interface WizardStep5ReviewProps {
  customerServicePhone?: string;
}

export function WizardStep5Review({
  customerServicePhone = '(800) 687-8629',
}: WizardStep5ReviewProps) {
  const {
    selectedUser,
    allergies,
    severity,
    conditions,
    paymentType,
    eprotectResponse,
    cardLast4,
    demographics,
    acknowledgeTerms,
    setAcknowledgeTerms,
    isSubmitting,
    submitError,
  } = useMailOrderStore();

  const allergyText = allergies.none
    ? 'None'
    : [trueKeys(allergies as unknown as Record<string, boolean | string>), allergies.other]
        .filter(Boolean)
        .join('; ');

  const severityText = severity.none
    ? 'None'
    : trueKeys(severity as unknown as Record<string, boolean | string>);

  const conditionText = conditions.none
    ? 'None'
    : [trueKeys(conditions as unknown as Record<string, boolean | string>), conditions.other]
        .filter(Boolean)
        .join('; ');

  const paymentText =
    paymentType === 'check'
      ? 'Check'
      : eprotectResponse?.paypageRegistrationId
        ? `Credit Card **** **** **** ${cardLast4}`
        : 'Credit Card (not captured)';

  const { phone, address } = demographics;
  const formattedPhoneNumber = phone.phoneNumber
    ? `${phone.phoneNumber.slice(0, 3)}-${phone.phoneNumber.slice(3)}`
    : '';
  const phoneText = [phone.areaCode, formattedPhoneNumber].filter(Boolean).join('-') + (phone.extension ? ` Ext. ${phone.extension}` : '');
  const addressLine1 = [address.street1, address.street2].filter(Boolean).join(', ');
  const addressLine2 = [address.city, address.state, address.zipCode].filter(Boolean).join(', ');

  return (
    <div>
      {/* ── Important Information ── */}
      <div
        style={{
          backgroundColor: '#fff8f0',
          border: '1px solid #ffd8a8',
          borderRadius: 8,
          padding: '16px 20px',
          marginBottom: 20,
        }}
      >
        <h5 style={{ color: '#854d0e', marginBottom: 12, fontWeight: 700 }}>
          &#9432;&nbsp; Important Information
        </h5>
        <p style={{ fontSize: '1.5rem', marginBottom: 8 }}>
          The submission of this form, for you or any of your dependents, authorizes the release of
          all information to the Plan Sponsor, Administrator, or Underwriter, and authorizes VytlOne
          to fill prescriptions with generic equivalents when available and permissible by law, in
          accordance with your benefit plan requirements. If you request a brand name drug when your
          doctor permits generic substitution, you may be responsible for paying the difference in
          cost between the brand name drug and the generic equivalent in addition to your co-payment.
          Refer to your plan benefit information for more details or contact customer service at{' '}
          {customerServicePhone}.
        </p>
        <p style={{ fontSize: '1.5rem', marginBottom: 8 }}>
          <strong>Reminder:</strong> You will always be charged the home delivery co-payment when
          you send or transfer a prescription to VytlOne Home Delivery. To maximize your savings,
          ask your doctor for a 90 day supply with refills up to one year. Please submit refill
          requests 14 days before your medication runs out. When paying by check or money order,
          please send the appropriate co-payment to avoid delays processing your request.
        </p>
        <hr style={{ borderColor: '#dee2e6', margin: '12px 0' }} />
        <p style={{ fontSize: '1.5rem', marginBottom: 8 }}>
          Written information about your prescriptions will be provided to you. Please read the
          information before taking any prescriptions. If you have questions, a pharmacist is
          available during normal business hours.
        </p>
        <p style={{ marginTop: 8, marginBottom: 4 }}>
          <strong>Complaints against the practice of the pharmacy may be filed with the:</strong>{' '}
          Texas State Board of Pharmacy William P. Hobby Building, Suite 3-600 333 Guadalupe, Box
          21 Austin, Texas 78701-3942 &bull; (512) 305-8000 To receive a complaint form, call
          (800) 821-3205 or (512) 305-8080 if in Austin. (recorded information only)
          www.tsbp.state.tx.us
        </p>
        <p style={{ marginBottom: 0 }}>
          La informaci&oacute;n escrita sobre sus medicamentos recetados ser&aacute; proporcionado
          a usted. Por favor, lea la informaci&oacute;n antes de tomar cualquier
          prescripci&oacute;n. Si tiene alguna pregunta, un farmac&eacute;utico est&aacute;
          disponible durante horas normales de oficina.{' '}
          <strong>Quejas contra la practica de la farmacia pueden ser reportadas al:</strong>{' '}
          Concilio de Farmacia Del Estado De Tejas William P. Hobby Building, Suite 3-600 333
          Guadalupe, Box 21 Austin, Texas 78701-3942 &bull; (512) 305-8000 Para recibir una forma
          de queja llame: (800) 821-3205 or (512) 305-8080 if in Austin. (informacion grabada
          solamente) www.tsbp.state.tx.us
        </p>
      </div>

      {/* Activation warning */}
      <div
        style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
        }}
      >
        <span style={{ fontSize: 20, lineHeight: 1.2 }}>&#9888;</span>
        <p style={{ margin: 0, fontSize: '1.5rem', color: '#664d03' }}>
          <strong>Activation takes up to two business days.</strong> You will not be able to process
          refills until your account activation is complete.
        </p>
      </div>

      {/* Acknowledgement */}
      <div
        style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fca5a5',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 24,
        }}
      >
        <label
          htmlFor="acknowledgeTerms"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: isSubmitting ? 'default' : 'pointer',
            margin: 0,
            fontWeight: 600,
            color: '#991b1b',
          }}
        >
          <input
            id="acknowledgeTerms"
            type="checkbox"
            style={{
              width: 18,
              height: 18,
              flexShrink: 0,
              cursor: isSubmitting ? 'default' : 'pointer',
              accentColor: '#dc2626',
            }}
            checked={acknowledgeTerms}
            onChange={e => setAcknowledgeTerms(e.target.checked)}
            disabled={isSubmitting}
          />
          I understand and agree to the terms above.
        </label>
      </div>

      {/*
       * BEST PRACTICE NOTE: The legacy wizard submitted immediately after the disclaimer
       * with no confirmation of the entered data. The review summary below is a deliberate
       * improvement — it gives users a final chance to catch data-entry errors before
       * submitting the enrollment. This is not present in the legacy AngularJS wizard.
       */}

      {/* Review & Submit heading */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <h4 style={{ margin: 0, fontWeight: 700 }}>Review &amp; Submit</h4>
        <span className="badge" style={{ backgroundColor: '#0d6efd', color: 'white', fontSize: '0.75rem', padding: '4px 10px', borderRadius: 12 }}>
          Step 5 of 5
        </span>
      </div>
      <p className="text-muted mb-4" style={{ fontSize: '0.9rem' }}>
        Review your information below before submitting. Go back to any step to make corrections.
      </p>

      <div className="row mx-0" style={{ marginBottom: '10px' }}>
        {/* Member */}
        <div className="col-sm-6 mb-3">
          <div
            style={{
              border: '1px solid #dee2e6',
              borderRadius: 8,
              overflow: 'hidden',
              height: '100%',
            }}
          >
            <div style={{ backgroundColor: '#f0f4ff', borderBottom: '1px solid #dee2e6', padding: '8px 14px' }}>
              <h6 style={{ margin: 0, fontWeight: 700, color: '#1e3a8a', fontSize: '1.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                &#128100;&nbsp; Member
              </h6>
            </div>
            <div style={{ padding: '12px 14px' }}>
              <SummaryRow label="Name" value={selectedUser?.displayName ?? ''} />
            </div>
          </div>
        </div>

        {/* Payment */}
        <div className="col-sm-6 mb-3">
          <div
            style={{
              border: '1px solid #dee2e6',
              borderRadius: 8,
              overflow: 'hidden',
              height: '100%',
            }}
          >
            <div style={{ backgroundColor: '#f0fff4', borderBottom: '1px solid #dee2e6', padding: '8px 14px' }}>
              <h6 style={{ margin: 0, fontWeight: 700, color: '#14532d', fontSize: '1.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                &#128179;&nbsp; Payment
              </h6>
            </div>
            <div style={{ padding: '12px 14px' }}>
              <SummaryRow label="Method" value={paymentText} />
            </div>
          </div>
        </div>
      </div>

    <div className="row mx-0">
        {/* Health Profile */}
        <div className="col-sm-6 mb-3">
          <div
            style={{
              border: '1px solid #dee2e6',
              borderRadius: 8,
              overflow: 'hidden',
              height: '100%',
            }}
          >
            <div style={{ backgroundColor: '#fdf4ff', borderBottom: '1px solid #dee2e6', padding: '8px 14px' }}>
              <h6 style={{ margin: 0, fontWeight: 700, color: '#581c87', fontSize: '1.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                &#129657;&nbsp; Health Profile
              </h6>
            </div>
            <div style={{ padding: '12px 14px' }}>
              <SummaryRow label="Allergies" value={allergyText} />
              <SummaryRow label="Severity" value={severityText} />
              <SummaryRow label="Conditions" value={conditionText} />
            </div>
          </div>
        </div>

        {/* Contact & Shipping */}
        <div className="col-sm-6 mb-3">
          <div
            style={{
              border: '1px solid #dee2e6',
              borderRadius: 8,
              overflow: 'hidden',
              height: '100%',
            }}
          >
            <div style={{ backgroundColor: '#fffbeb', borderBottom: '1px solid #dee2e6', padding: '8px 14px' }}>
              <h6 style={{ margin: 0, fontWeight: 700, color: '#78350f', fontSize: '1.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                &#128205;&nbsp; Contact &amp; Shipping
              </h6>
            </div>
            <div style={{ padding: '12px 14px' }}>
              <SummaryRow label="Phone" value={phoneText} />
              <SummaryRow label="Address" value={addressLine1} />
              <SummaryRow label="" value={addressLine2} />
            </div>
          </div>
        </div>
      </div>

      {submitError && (
        <div className="alert alert-danger py-2 small mt-2" role="alert">
          {submitError}
        </div>
      )}
    </div>
  );
}
